class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // The is-empty state can be toggled client-side (last item removed)
        // while the server-rendered empty markup is absent - fall back.
        const containerToTrapFocusOn =
          (this.classList.contains('is-empty') && this.querySelector('.drawer__inner-empty')) ||
          document.getElementById('CartDrawer') ||
          this;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');

    /* The is-empty class that hides .cart__contents sits on <cart-drawer>
       itself, and the line above only clears it from .drawer__inner (where it
       never is). Callers used to compensate one by one - product-form.js still
       does - so any other caller left the drawer stuck in its empty state after
       the first item was added. The state is taken from the freshly rendered
       section, which also covers the cart going back to empty. */
    const drawerSection = parsedState && parsedState.sections && parsedState.sections['cart-drawer'];
    let shouldBeEmpty = null;
    if (drawerSection) {
      const sourceDrawer = this.getSectionDOM(drawerSection, 'cart-drawer');
      if (sourceDrawer) shouldBeEmpty = sourceDrawer.classList.contains('is-empty');
    }
    if (shouldBeEmpty === null && parsedState && typeof parsedState.item_count === 'number') {
      shouldBeEmpty = parsedState.item_count === 0;
    }
    if (shouldBeEmpty !== null) this.classList.toggle('is-empty', shouldBeEmpty);

    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);


class FreeShippingBar extends HTMLElement {
  connectedCallback() {
    subscribe(PUB_SUB_EVENTS.cartUpdate, this._onCartUpdated.bind(this));
  }

  get threshold() {
    return parseFloat(this.getAttribute('threshold'));
  }

  _onCartUpdated(event) {
    const totalPrice = event.cartData ? event.cartData.total_price : 0;
    this.style.setProperty('--progress', Math.min(totalPrice / this.threshold, 1));
  }
}

customElements.define('free-shipping-bar', FreeShippingBar);

class CartDrawerRecommendations extends HTMLElement {
  connectedCallback() {
    this._initializeRecommendations(this.dataset.productId);
  }

  _initializeRecommendations(productId) {
    if (this._observer) this._observer.unobserve(this);
    this._observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this._loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this._observer.observe(this);
  }

  _loadRecommendations(productId) {
    if (!productId) return;
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('cart-drawer-recommendations');
        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
          this.classList.add('recommendations--loaded');
          this._initSwiper();
        }
      })
      .catch((e) => console.error(e));
  }

  _initSwiper() {
    const swiperEl = this.querySelector('.swiper--cdr');
    if (!swiperEl || typeof Swiper === 'undefined') return;
    new Swiper(swiperEl, {
      slidesPerView: 1.2,
      freeMode: false,
      spaceBetween: 15,
      pagination: {
        el: '.swiper-pagination',
        clickable: true,
      },
    });
  }
}

customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
